<!-- Type-driven Collaboratori subsection (collapsible cards, stile allineato agli Impiegati) -->
@if (!isHidden()) {
  <section class="surface-0 border-round p-5 mb-4 shadow-1" role="region" aria-labelledby="collab-subsection-title">
    <div class="flex align-items-center gap-2 mb-3">
      <i class="pi pi-users text-xl" style="color: #00125b;" aria-hidden="true"></i>
      <h2 id="collab-subsection-title" class="text-xl font-semibold m-0">Collaboratori Professionisti</h2>
    </div>

    @if (!readonly()) {
      <div class="surface-100 border-round p-3 mb-3" role="region" aria-labelledby="collab-notice-title">
        <p id="collab-notice-title" class="text-sm text-700 m-0">
          <i class="pi pi-exclamation-triangle mr-2" style="color: #f59e0b;" aria-hidden="true"></i>
          <strong>Nota importante:</strong> Funzione disponibile solo per il ruolo di “Collaboratori Professionisti”. Per gli “Impiegati Amministrativi”, che necessitano di accedere ai sistemi gestionali sinistri di AXA, procedere con la sezione successiva.
        </p>
      </div>
      <div class="surface-100 border-round p-3 mb-4" role="region" aria-labelledby="collab-instructions-title">
        <p id="collab-instructions-title" class="text-sm text-700 m-0">
          <i class="pi pi-info-circle mr-2" aria-hidden="true"></i>
          <strong>Istruzioni:</strong> Aggiungi uno o più collaboratori professionisti compilando tutti i campi obbligatori (contrassegnati con *). Puoi comprimere ciascuna scheda: da chiusa mostra solo cognome e nome.
        </p>
      </div>
    }

    @for (collab of collaborators(); track collab.id; let i = $index) {
      <div class="surface-0 border-round p-4 mb-3 shadow-1">
        <!-- Header Card con numerazione + toggle collasso -->
        <div class="surface-100 border-round p-3 border-1"
             [class.border-300]="!collab.fiscalCodeError"
             [class.border-red-500]="collab.fiscalCodeError"
             [class.mb-4]="collab.expanded">
          <div class="flex justify-content-between align-items-center gap-2">
            <button
              type="button"
              class="flex align-items-center gap-2 flex-1 border-none bg-transparent p-0 cursor-pointer text-left"
              (click)="toggleExpanded(collab.id)"
              [attr.aria-expanded]="collab.expanded"
              [attr.aria-label]="(collab.expanded ? 'Comprimi' : 'Espandi') + ' collaboratore professionista ' + (i + 1)"
            >
              <i class="pi" [ngClass]="collab.expanded ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true"></i>
              <i class="pi pi-user text-xl" style="color: #00125b;" aria-hidden="true"></i>
              <h4 class="text-lg font-semibold m-0">
                Collaboratore Professionista #{{ i + 1 }}
                @if (collapsedTitle(collab); as title) {
                  <span class="font-normal text-600"> — {{ title }}</span>
                }
              </h4>
            </button>
            @if (!readonly()) {
              <button
                pButton
                type="button"
                icon="pi pi-trash"
                class="p-button-text p-button-danger p-button-sm"
                (click)="removeCollaborator(collab.id)"
                [attr.aria-label]="'Rimuovi collaboratore professionista ' + (i + 1)"
                pTooltip="Rimuovi collaboratore professionista"
              ></button>
            }
          </div>
        </div>

        @if (collab.expanded) {
          <div class="grid">
            <!-- Cognome -->
            <div class="field col-12 md:col-4">
              <label [for]="'collab-cognome-' + collab.id" class="font-semibold block mb-2">
                Cognome <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                type="text"
                [id]="'collab-cognome-' + collab.id"
                [(ngModel)]="collab.lastName"
                class="w-full"
                [class.p-invalid]="collab.lastNameError && isMissingMeaningfulText(collab.lastName)"
                [disabled]="readonly()"
              />
            </div>

            <!-- Nome -->
            <div class="field col-12 md:col-4">
              <label [for]="'collab-nome-' + collab.id" class="font-semibold block mb-2">
                Nome <span class="text-red-500" aria-hidden="true">*</span>
              </label>
              <input
                pInputText
                type="text"
                [id]="'collab-nome-' + collab.id"
                [(ngModel)]="collab.firstName"
                class="w-full"
                [class.p-invalid]="collab.firstNameError && isMissingMeaningfulText(collab.firstName)"
                [disabled]="readonly()"
              />
            </div>

            <!-- Codice Fiscale -->
            <div class="field col-12 md:col-4">
              <label [for]="'collab-cf-' + collab.id" class="font-semibold block mb-2">
                Codice Fiscale <span class="text-red-500" aria-hidden="true">*</span>
                <i
                  class="pi pi-info-circle ml-2 text-primary cursor-help"
                  pTooltip="16 caratteri per persone fisiche, 11 cifre per aziende"
                  tooltipPosition="bottom"
                  tabindex="0"
                  role="img"
                  aria-label="Il codice fiscale deve essere di 16 caratteri per persone fisiche o 11 cifre per aziende"
                ></i>
              </label>
              <input
                pInputText
                type="text"
                [id]="'collab-cf-' + collab.id"
                [ngModel]="collab.fiscalCode"
                (ngModelChange)="collab.fiscalCode = $event; collab.fiscalCodeError = ''"
                class="w-full"
                [class.p-invalid]="collab.fiscalCodeError"
                [disabled]="readonly()"
              />
              @if (collab.fiscalCodeError) {
                <small class="text-red-500 block mt-1" role="alert">{{ collab.fiscalCodeError }}</small>
              }
            </div>

            <!-- Numero iscrizione albo nazionale / Ruolo Periti Assicurativi (label per tipo) -->
            @if (config().showRegistrationNumber) {
              <div class="field col-12 md:col-6">
                <label [for]="'collab-reg-' + collab.id" class="font-semibold block mb-2">
                  {{ registrationNumberLabel() }} <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  pInputText
                  type="text"
                  [id]="'collab-reg-' + collab.id"
                  [(ngModel)]="collab.registrationNumber"
                  class="w-full"
                  [class.p-invalid]="collab.registrationNumberError && isMissingMeaningfulText(collab.registrationNumber)"
                  [disabled]="readonly()"
                />
              </div>
            }

            <!-- Ordine professionale -->
            @if (config().showProfessionalOrder) {
              <div class="field col-12 md:col-6">
                <label [for]="'collab-order-' + collab.id" class="font-semibold block mb-2">
                  Ordine professionale <span class="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  pInputText
                  type="text"
                  [id]="'collab-order-' + collab.id"
                  [(ngModel)]="collab.professionalOrder"
                  class="w-full"
                  [class.p-invalid]="collab.professionalOrderError && isMissingMeaningfulText(collab.professionalOrder)"
                  [disabled]="readonly()"
                />
              </div>
            }

            <!-- Dipendente pubblico (solo DOCTOR) -->
            @if (config().showPublicWork) {
              <fieldset class="field col-12 md:col-6">
                <legend class="font-semibold block mb-2">Dipendente pubblico</legend>
                <div class="flex gap-3" role="group">
                  <div class="flex align-items-center gap-2">
                    <p-radioButton
                      [inputId]="'collab-civil-yes-' + collab.id"
                      [name]="'collab-civil-' + collab.id"
                      [value]="true"
                      [ngModel]="collab.civilServant"
                      (ngModelChange)="onCivilServantChange(collab, true)"
                      [disabled]="readonly()"
                    ></p-radioButton>
                    <label [for]="'collab-civil-yes-' + collab.id" class="cursor-pointer">Sì</label>
                  </div>
                  <div class="flex align-items-center gap-2">
                    <p-radioButton
                      [inputId]="'collab-civil-no-' + collab.id"
                      [name]="'collab-civil-' + collab.id"
                      [value]="false"
                      [ngModel]="collab.civilServant"
                      (ngModelChange)="onCivilServantChange(collab, false)"
                      [disabled]="readonly()"
                    ></p-radioButton>
                    <label [for]="'collab-civil-no-' + collab.id" class="cursor-pointer">No</label>
                  </div>
                </div>
              </fieldset>

              @if (collab.civilServant) {
                <div class="field col-12 md:col-6">
                  <label [for]="'collab-body-' + collab.id" class="font-semibold block mb-2">
                    Ente pubblico <span class="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    pInputText
                    type="text"
                    [id]="'collab-body-' + collab.id"
                    [(ngModel)]="collab.publicBody"
                    class="w-full"
                    [class.p-invalid]="collab.publicBodyError && isMissingMeaningfulText(collab.publicBody)"
                    [disabled]="readonly()"
                  />
                </div>
                <div class="field col-12 md:col-6">
                  <label [for]="'collab-office-' + collab.id" class="font-semibold block mb-2">
                    Ufficio di comunicazione <span class="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    pInputText
                    type="text"
                    [id]="'collab-office-' + collab.id"
                    [(ngModel)]="collab.communicationOffice"
                    class="w-full"
                    [class.p-invalid]="collab.communicationOfficeError && isMissingMeaningfulText(collab.communicationOffice)"
                    [disabled]="readonly()"
                  />
                </div>
              }
            }

            <div class="field col-12 md:col-6">
              <app-phone-input
                [inputId]="'collab-mobile-' + collab.id"
                label="Cellulare"
                [(prefix)]="collab.mobilePhonePrefix"
                [(number)]="collab.mobilePhone"
                [disabled]="readonly()"
                [forceShowError]="contactValidationRequested()"
              />
            </div>

            <div class="field col-12 md:col-6">
              <app-phone-input
                [inputId]="'collab-telephone-' + collab.id"
                label="Telefono"
                [(prefix)]="collab.telephonePrefix"
                [(number)]="collab.telephone"
                [disabled]="readonly()"
                [forceShowError]="contactValidationRequested()"
                placeholder="es. 0612345678"
              />
            </div>

            <div class="col-12 claims-phones-section">
              <div class="claims-phones-panel" role="group" aria-label="Numeri riservati alla rete liquidativa">
                <div class="field claims-phones-panel__field claims-phones-panel__primary">
                  <app-phone-input
                    [inputId]="'collab-claims-phone-' + collab.id"
                    label="Numero di cellulare/telefono riservato alla rete liquidativa"
                    description="Numero riservato alla compagnia per comunicazioni urgenti"
                    [(prefix)]="collab.claimsNetworkPhonePrefix"
                    [(number)]="collab.claimsNetworkPhone"
                    [disabled]="readonly()"
                    [forceShowError]="contactValidationRequested()"
                  />
                </div>
                @if (!readonly()) {
                  <p-button
                    type="button"
                    label="Aggiungi numero di cellulare/telefono"
                    icon="pi pi-plus"
                    [outlined]="true"
                    class="claims-phones-panel__add"
                    [disabled]="!canAddClaimsPhone(collab)"
                    [attr.data-testid]="'add-claims-phone-' + collab.id"
                    (onClick)="addClaimsPhone(collab)"
                  />
                }
                @for (phone of collab.additionalClaimsNetworkPhones; track phone.id; let phoneIndex = $index) {
                  <div class="field claims-phones-panel__field">
                    <app-phone-input
                      [inputId]="'collab-additional-claims-phone-' + collab.id + '-' + phone.id"
                      [label]="'Numero aggiuntivo riservato alla rete liquidativa ' + (phoneIndex + 1)"
                      description="Numero riservato alla compagnia per comunicazioni urgenti"
                      [(prefix)]="phone.prefix"
                      [(number)]="phone.number"
                      [disabled]="readonly()"
                      [forceShowError]="contactValidationRequested()"
                    />
                    @if (isDuplicateClaimsPhone(collab, phone)) {
                      <small class="text-red-500 block mt-1" role="alert">Numero gia presente per questo collaboratore.</small>
                    }
                  </div>
                  @if (!readonly()) {
                    <p-button
                      type="button"
                      icon="pi pi-trash"
                      severity="danger"
                      [text]="true"
                      class="claims-phones-panel__remove"
                      [attr.data-testid]="'remove-claims-phone-' + phone.id"
                      [ariaLabel]="'Elimina numero aggiuntivo ' + (phoneIndex + 1)"
                      pTooltip="Elimina numero"
                      (onClick)="removeClaimsPhone(collab, phone.id)"
                    />
                  }
                }
              </div>
            </div>

            <div class="field col-12 md:col-6">
              <label [for]="'collab-contact-email-' + collab.id" class="font-semibold block mb-2">Email di contatto</label>
              <input
                pInputText
                type="email"
                [id]="'collab-contact-email-' + collab.id"
                [(ngModel)]="collab.contactEmail"
                class="w-full"
                maxlength="255"
                autocomplete="email"
                [disabled]="readonly()"
                [class.p-invalid]="contactValidationRequested() && !isValidContactEmail(collab.contactEmail)"
                [attr.aria-invalid]="contactValidationRequested() && !isValidContactEmail(collab.contactEmail)"
                [attr.aria-describedby]="contactValidationRequested() && !isValidContactEmail(collab.contactEmail) ? 'collab-contact-email-error-' + collab.id : null"
              />
              @if (contactValidationRequested() && !isValidContactEmail(collab.contactEmail)) {
                <small [id]="'collab-contact-email-error-' + collab.id" class="text-red-500 block mt-1" role="alert">Formato email non valido</small>
              }
            </div>

            <!-- Accesso ai sistemi gestionali -->
            <fieldset class="field col-12 md:col-6">
              <legend class="font-semibold block mb-2">Necessita accesso al portale sinistri</legend>
              <div class="flex gap-3" role="group">
                <div class="flex align-items-center gap-2">
                  <p-radioButton
                    [inputId]="'collab-access-yes-' + collab.id"
                    [name]="'collab-access-' + collab.id"
                    [value]="true"
                    [ngModel]="collab.claimsSystemAccess"
                    (ngModelChange)="onAccessChange(collab, true)"
                    [disabled]="readonly()"
                  ></p-radioButton>
                  <label [for]="'collab-access-yes-' + collab.id" class="cursor-pointer">Sì</label>
                </div>
                <div class="flex align-items-center gap-2">
                  <p-radioButton
                    [inputId]="'collab-access-no-' + collab.id"
                    [name]="'collab-access-' + collab.id"
                    [value]="false"
                    [ngModel]="collab.claimsSystemAccess"
                    (ngModelChange)="onAccessChange(collab, false)"
                    [disabled]="readonly()"
                  ></p-radioButton>
                  <label [for]="'collab-access-no-' + collab.id" class="cursor-pointer">No</label>
                </div>
              </div>
            </fieldset>
          </div>
        }
      </div>
    } @empty {
      <div class="surface-100 border-round p-5 text-center">
        <i class="pi pi-info-circle text-5xl mb-3" style="color: #00125b;" aria-hidden="true"></i>
        <h3 class="text-xl font-semibold mb-2 text-800">Nessun collaboratore professionista aggiunto</h3>
        @if (!readonly()) {
          <p class="text-600 mb-0">
            Clicca sul pulsante "Aggiungi collaboratore professionista" per iniziare ad inserire i dati dei tuoi collaboratori professionisti.
          </p>
        }
      </div>
    }

    @if (!readonly()) {
      <div class="flex justify-content-end mt-4">
        <button
          pButton
          type="button"
          icon="pi pi-plus"
          label="Aggiungi collaboratore professionista"
          class="p-button-outlined"
          (click)="addCollaborator()"
        ></button>
      </div>
    }
  </section>
}
